import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  envFeatureFlags,
  mergeModuleFlagsFromApi,
  type ModuleFeatureFlags,
  type PublicModuleSettingsPayload,
} from "@/lib/feature-flags";

type ModuleFlagsContextValue = {
  flags: ModuleFeatureFlags;
  isLoading: boolean;
  refresh: () => Promise<void>;
};

const ModuleFlagsContext = createContext<ModuleFlagsContextValue | null>(null);

async function fetchPublicModuleSettings(): Promise<PublicModuleSettingsPayload | null> {
  try {
    const response = await fetch("/api/v1/public/settings/user");
    if (!response.ok) return null;
    return (await response.json()) as PublicModuleSettingsPayload;
  } catch {
    return null;
  }
}

export function ModuleFlagsProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<ModuleFeatureFlags>(envFeatureFlags);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    const api = await fetchPublicModuleSettings();
    setFlags(mergeModuleFlagsFromApi(envFeatureFlags, api));
    setIsLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      flags,
      isLoading,
      refresh,
    }),
    [flags, isLoading, refresh],
  );

  return <ModuleFlagsContext.Provider value={value}>{children}</ModuleFlagsContext.Provider>;
}

export function useModuleFlags(): ModuleFlagsContextValue {
  const ctx = useContext(ModuleFlagsContext);
  if (!ctx) {
    return {
      flags: envFeatureFlags,
      isLoading: false,
      refresh: async () => undefined,
    };
  }
  return ctx;
}
